import BaseController from "./base_controller.js";
import { api, APIError, API_BASE_URL } from "/js/services/api.js";

const PJ_LABELS = {
  identity: "Pièce d'identité",
  france_travail_attestation: "Attestation France Travail",
  rib: "RIB",
};
const ALL_PJ_TYPES = ["identity", "france_travail_attestation", "rib"];
const MAX_PJ_BYTES = 10 * 1024 * 1024;

const DATE_FMT_FR = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric", month: "long", year: "numeric",
});

/**
 * EPIC-020 / 20.4 — Page « Demande ACRE » §10.A
 *
 * État porté par le controller :
 *   _request : { id, responseStatus, cerfaPdfUrl, attachmentPaths,
 *                sentAt, acceptanceReceivedAt, refusalReceivedAt,
 *                refusalReason } | null
 *
 * Le statut administratif est `responseStatus` (not_sent / sent /
 * accepted / refused). Le bouton « J'ai envoyé ma demande » bascule
 * vers `sent` ; le bouton « Demande acceptée/refusée » vers
 * `accepted | refused` (avec date d'effet pour accepted).
 */
export default class AcreDemandeController extends BaseController {
  static targets = [
    "errorBanner", "errorMessage",
    "statusName", "statusDesc",
    "beneficiary", "siret", "startDate", "deadlineHint", "urssaf",
    "downloadCerfaBtn",
    "pjIdentityInput", "pjAttestationInput", "pjRibInput",
    "uploadedSummary", "uploadedList", "uploadedItemTemplate",
    "downloadZipBtn", "markSentBtn",
    "trackingBlock", "trackingName", "trackingLines",
    "trackingSentTemplate", "trackingAcceptedTemplate", "trackingRefusedTemplate",
    "outcomeRefusedBtn", "outcomeAcceptedBtn",
    "supportCorrectionLink",
  ];

  async connect() {
    this._request = null;
    this._isUploading = new Set();
    await this._loadCurrent();
  }

  // ---------------------------------------------------------------------------
  // Chargement état initial.
  // ---------------------------------------------------------------------------

  async _loadCurrent() {
    try {
      const data = await api.get("/acre/request/current");
      this._request = data;
    } catch (error) {
      if (error instanceof APIError && error.status === 404) {
        await this._initRequest();
        return;
      }
      this._showError(this._extractMessage(error));
      return;
    }
    this._render();
  }

  async _initRequest() {
    try {
      const data = await api.post("/acre/request", {});
      this._request = data;
    } catch (error) {
      this._showError(this._extractMessage(error));
      return;
    }
    this._render();
  }

  // ---------------------------------------------------------------------------
  // Actions utilisateur.
  // ---------------------------------------------------------------------------

  downloadCerfa(event) {
    event?.preventDefault?.();
    if (!this._request?.cerfaPdfUrl) return;
    window.open(this._request.cerfaPdfUrl, "_blank", "noopener");
  }

  uploadIdentity(event) { return this._upload(event, "identity"); }
  uploadAttestation(event) { return this._upload(event, "france_travail_attestation"); }
  uploadRib(event) { return this._upload(event, "rib"); }

  async _upload(event, pjType) {
    const input = event?.target;
    if (!(input instanceof HTMLInputElement)) return;
    const file = input.files?.[0];
    if (!file || !this._request) return;

    if (file.size > MAX_PJ_BYTES) {
      this._showError(`${PJ_LABELS[pjType]} — fichier supérieur à 10 Mo.`);
      input.value = "";
      return;
    }
    if (this._isUploading.has(pjType)) return;
    this._isUploading.add(pjType);
    this._hideError();

    const formData = new FormData();
    formData.append("pjType", pjType);
    formData.append("file", file);

    try {
      const response = await fetch(`${API_BASE_URL}/acre/request/${this._request.id}/pj`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!response.ok) {
        await this._handleFetchError(response);
        return;
      }
      await this._loadCurrent();
    } catch {
      this._showError("Le téléversement de la pièce a échoué. Réessayez.");
    } finally {
      this._isUploading.delete(pjType);
      input.value = "";
    }
  }

  async downloadZip(event) {
    event?.preventDefault?.();
    if (!this._request) return;
    this._hideError();
    try {
      const data = await api.post(`/acre/request/${this._request.id}/generate-zip`, {});
      if (data?.zipUrl) {
        window.open(data.zipUrl, "_blank", "noopener");
      }
    } catch (error) {
      this._showError(this._extractMessage(error));
    }
  }

  async markAsSent(event) {
    event?.preventDefault?.();
    if (!this._request) return;
    this._hideError();
    try {
      const data = await api.post(`/acre/request/${this._request.id}/sent`, {});
      this._request = data;
    } catch (error) {
      this._showError(this._extractMessage(error));
      return;
    }
    this._render();
  }

  async recordAccepted(event) {
    event?.preventDefault?.();
    if (!this._request) return;

    const isoDefault = new Date().toISOString().slice(0, 10);
    const input = window.prompt(
      "Date d'effet de l'ACRE (format YYYY-MM-DD) :",
      isoDefault,
    );
    if (!input) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(input)) {
      this._showError("Date d'effet invalide. Attendu : YYYY-MM-DD.");
      return;
    }

    this._hideError();
    try {
      const data = await api.post(`/acre/request/${this._request.id}/outcome`, {
        outcome: "accepted",
        effectiveDate: input,
      });
      this._request = data;
    } catch (error) {
      this._showError(this._extractMessage(error));
      return;
    }
    this._render();
  }

  async recordRefused(event) {
    event?.preventDefault?.();
    if (!this._request) return;

    const reason = window.prompt(
      "Motif du refus (optionnel) :",
      "",
    );
    if (reason === null) return; // l'utilisateur a annulé

    this._hideError();
    try {
      const data = await api.post(`/acre/request/${this._request.id}/outcome`, {
        outcome: "refused",
        refusalReason: reason || null,
      });
      this._request = data;
    } catch (error) {
      this._showError(this._extractMessage(error));
      return;
    }
    this._render();
  }

  async requestSupportCorrection(event) {
    event?.preventDefault?.();
    this._hideError();
    try {
      const data = await api.post("/acre/request/support-correction", {});
      if (data?.mailtoLink) {
        window.location.assign(data.mailtoLink);
      }
    } catch (error) {
      this._showError(this._extractMessage(error));
    }
  }

  // ---------------------------------------------------------------------------
  // Rendu.
  // ---------------------------------------------------------------------------

  _render() {
    const r = this._request;
    if (!r) return;

    const status = r.responseStatus ?? "not_sent";

    // Bouton « J'ai envoyé ma demande » désactivé tant qu'au moins une PJ requise manque.
    const uploadedTypes = new Set((r.attachmentPaths ?? []).map((e) => e.pjType));
    const missingPj = ALL_PJ_TYPES.filter((t) => !uploadedTypes.has(t));
    const allUploaded = missingPj.length === 0;

    this._setDisabled(this.markSentBtnTarget, !allUploaded || status !== "not_sent");
    this._setDisabled(this.downloadZipBtnTarget, !allUploaded);

    // Liste « Pièces téléversées ».
    this._renderUploadedList(r.attachmentPaths ?? []);

    // Bloc suivi (visible dès qu'on a basculé sent).
    if (status === "not_sent") {
      this.trackingBlockTarget.hidden = true;
    } else {
      this.trackingBlockTarget.hidden = false;
      this._renderTracking(r);
    }

    // Désactivation des inputs PJ après envoi.
    if (status !== "not_sent") {
      this.pjIdentityInputTarget.disabled = true;
      this.pjAttestationInputTarget.disabled = true;
      this.pjRibInputTarget.disabled = true;
    }

    // Confirmation outcome désactivée hors statut `sent`.
    const canRecordOutcome = status === "sent";
    this._setDisabled(this.outcomeAcceptedBtnTarget, !canRecordOutcome);
    this._setDisabled(this.outcomeRefusedBtnTarget, !canRecordOutcome);
  }

  _renderUploadedList(attachments) {
    const list = this.uploadedListTarget;
    list.replaceChildren();

    const filtered = attachments.filter((e) => ALL_PJ_TYPES.includes(e.pjType));
    if (filtered.length === 0) {
      const empty = document.createElement("div");
      empty.className = "nm-infoblock__lines";
      empty.textContent = "Aucune pièce téléversée pour l'instant.";
      list.appendChild(empty);
      return;
    }

    const tpl = this.uploadedItemTemplateTarget;
    for (const entry of filtered) {
      const node = tpl.content.firstElementChild.cloneNode(true);
      const label = node.querySelector('[data-slot="pjLabel"]');
      const uploadedAt = node.querySelector('[data-slot="uploadedAt"]');
      if (label) label.textContent = PJ_LABELS[entry.pjType] ?? entry.pjType;
      if (uploadedAt) uploadedAt.textContent = this._formatDate(entry.uploadedAt);
      list.appendChild(node);
    }

    const uploadedTypes = new Set(filtered.map((e) => e.pjType));
    const missing = ALL_PJ_TYPES.filter((t) => !uploadedTypes.has(t)).map((t) => PJ_LABELS[t]);
    if (missing.length > 0) {
      const note = document.createElement("div");
      note.className = "nm-infoblock__lines";
      note.textContent = `Pièces encore à fournir : ${missing.join(", ")}.`;
      list.appendChild(note);
    }
  }

  _renderTracking(request) {
    const container = this.trackingLinesTarget;
    container.replaceChildren();

    switch (request.responseStatus) {
      case "sent": {
        this.trackingNameTarget.textContent = "Demande envoyée — en attente de réponse";
        const node = this.trackingSentTemplateTarget.content.firstElementChild.cloneNode(true);
        const sentAt = node.querySelector('[data-slot="sentAt"]');
        if (sentAt) sentAt.textContent = request.sentAt ? this._formatDate(request.sentAt) : "—";
        container.appendChild(node);
        break;
      }
      case "accepted": {
        this.trackingNameTarget.textContent = "Demande acceptée";
        const node = this.trackingAcceptedTemplateTarget.content.firstElementChild.cloneNode(true);
        const acceptedAt = node.querySelector('[data-slot="acceptedAt"]');
        if (acceptedAt) acceptedAt.textContent = request.acceptanceReceivedAt ? this._formatDate(request.acceptanceReceivedAt) : "—";
        container.appendChild(node);
        break;
      }
      case "refused": {
        this.trackingNameTarget.textContent = "Demande refusée";
        const node = this.trackingRefusedTemplateTarget.content.firstElementChild.cloneNode(true);
        const refusedAt = node.querySelector('[data-slot="refusedAt"]');
        const refusedReason = node.querySelector('[data-slot="refusedReason"]');
        if (refusedAt) refusedAt.textContent = request.refusalReceivedAt ? this._formatDate(request.refusalReceivedAt) : "—";
        if (refusedReason && request.refusalReason) {
          refusedReason.textContent = `Motif : ${request.refusalReason}.`;
          refusedReason.hidden = false;
        }
        container.appendChild(node);
        break;
      }
      default:
        this.trackingBlockTarget.hidden = true;
    }
  }

  _setDisabled(button, disabled) {
    if (!(button instanceof HTMLButtonElement)) return;
    button.disabled = !!disabled;
    button.setAttribute("aria-disabled", disabled ? "true" : "false");
  }

  _formatDate(value) {
    if (!value) return "—";
    const date = value.length === 10 ? new Date(`${value}T00:00:00`) : new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return DATE_FMT_FR.format(date);
  }

  _extractMessage(error) {
    if (error instanceof APIError) return error.message;

    return "Une erreur est survenue. Réessayez dans un instant.";
  }

  async _handleFetchError(response) {
    if (response.status === 401) {
      window.location.assign("/connexion.html");
      return;
    }
    let message = `Erreur ${response.status}.`;
    try {
      const data = await response.json();
      if (data?.error?.message) message = data.error.message;
    } catch {
      // ignore
    }
    this._showError(message);
  }

  _showError(message) {
    if (!this.hasErrorBannerTarget) return;
    if (this.hasErrorMessageTarget) this.errorMessageTarget.textContent = message;
    this.errorBannerTarget.hidden = false;
  }

  _hideError() {
    if (!this.hasErrorBannerTarget) return;
    this.errorBannerTarget.hidden = true;
  }
}
